/**
 * Game screen — dispatches on game phase:
 *   • waiting → lobby room (settings + roster; owner can start/kick)
 *   • grace/running → zone indicator + HUD + action tiles (voice-first, tap as backup)
 *   • ended → result + leave
 */
import A from "aberdeen";
import * as S from "staffa";
import { store } from "../store";
import type { Me, Game, GamePlayer, ActionDef } from "../api-types";
import { drawSectionHeader } from "./theme";
import { bridge } from "../native/bridge";
import { $compass } from "../native/compass";
import { distM, bearingDeg } from "../geo";
import { $now } from "./clock";
import { target, timer, users, logOut, play, userX, arrowLeft, radio } from "staffa/icons.js";
import * as icons from "staffa/icons.js";
import type { IconOptions } from "staffa/icons.js";

type IconFn = (opts?: IconOptions) => void;
/** The full Lucide set, indexable by the icon names the server sends. */
const iconSet = icons as unknown as Record<string, IconFn | undefined>;

/** Currency symbol + theme colour for everything money-related. A not-quite-white
 *  with a slight green tint, so credits read as their own thing distinct from the
 *  neon-green primary. */
const CREDIT = "₡";
const CREDIT_COLOR = "#DCF2D6";

/**
 * Draw a server-named Lucide icon, falling back to `radio` for an unknown name
 * so the client stays agnostic to whatever roles/actions the server defines.
 */
function drawNamedIcon(name: string | undefined, opts: IconOptions = {}): void {
  ((name && iconSet[name]) || radio)(opts);
}

export function drawGame(me: Me | undefined, game: Game | undefined) {
  if (!game) return;
  // The waiting room is its own screen (unless we just left it).
  if (game.phase === "waiting" && !store.$.leftGame) return drawWaitingRoom(me, game);
  // "Out" = left, eliminated, or the game has finished. Such a player gets the
  // spectator/over screen (regular game layout, no action tiles) rather than the
  // live action screen.
  const dead = me?.currentPlayer?.statusLabel === "Dead";
  const out = store.$.leftGame || game.phase === "ended" || dead;
  if (out) return drawOverScreen(me, game);
  return drawActiveGame(me, game);
}

// ── Waiting room ───────────────────────────────────────────────────────────
function drawWaitingRoom(me: Me | undefined, game: Game) {
  const isOwner = game.owner?.id === me?.currentPlayer?.id;
  A("div display:flex flex-direction:column gap:$3", () => {
    // Reactive h1 — owner name can change before game starts
    A(() => A(`h1 #${game.owner?.name ?? "?"}'s game`));

    if (isOwner) drawOwnerSettings(game);
    else
      S.box({
        attrs: ".primary",
        contentAttrs: "fg:$s-muted letter-spacing:0.05em text-align:center",
        content: () => A("div #WAITING FOR HOST TO START…"),
      });

    drawActionConfig(game, isOwner);

    drawRoster(me, game, isOwner);

    drawAudioDebug();

    A("div display:flex gap:$2", () => {
      S.button({
        content: "LEAVE",
        icon: () => logOut({ size: "1.1em" }),
        attrs: ".neutral",
        click: () => store.leaveGame(),
      });
      if (isOwner) {
        A(() => {
          const count = game.players?.length ?? 0;
          S.button({
            content: "START GAME",
            icon: () => play({ size: "1.1em" }),
            attrs: "flex:1",
            disabled: count < 2,
            click: () => void store.startGame(),
          });
        });
      }
    });
  });
}

/** Debug panel: a live readout of the native audio route, so we can see on real
 *  hardware whether playback actually follows onto the Bluetooth SCO link. */
function drawAudioDebug() {
  S.box({
    attrs: ".neutral",
    contentAttrs: "display:flex flex-direction:column gap:$2",
    content: () => {
      drawSectionHeader("AUDIO DEBUG", { icon: radio });
      A(() =>
        A(
          `div font-family:monospace font-size:0.7rem fg:$s-muted word-break:break-all #${store.$.audioDebug || "(no route info)"}`,
        ),
      );
    },
  });
}

function drawOwnerSettings(game: Game) {
  S.box({
    attrs: ".primary",
    contentAttrs: "display:flex flex-direction:column gap:$3",
    content: () => {
      drawSlider(
        target,
        (v) => `ZONE RADIUS: ${v} M`,
        { min: 50, max: 2000, step: 50 },
        () => game.radiusM,
        (v) => store.setRange(v),
      );
      drawSlider(
        timer,
        (v) => `DURATION: ${Math.round(v / 60)} MIN`,
        { min: 120, max: 3600, step: 60 },
        () => game.durationS,
        (v) => store.setTime(v),
      );
    },
  });
}

/** Action configuration: the owner toggles which actions are enabled for the
 *  game; everyone else sees the enabled set read-only. The client knows nothing
 *  about actions beyond the server-provided catalogue (`store.$.catalog`). */
function drawActionConfig(game: Game, isOwner: boolean) {
  S.box({
    attrs: ".primary",
    contentAttrs: "display:flex flex-direction:column gap:$2",
    content: () => {
      drawSectionHeader("ACTIONS", { icon: radio });
      A(() => {
        const catalog = store.$.catalog;
        const enabled = new Set(game.enabledActions ?? []);
        if (!catalog.length) return A("div fg:$s-muted font-size:0.8rem #LOADING…");
        // 'ignite' is implied by 'explosive', so hide it from the toggle list.
        // Non-owners also never see locked add-ons (ones not available to them
        // that the host hasn't enabled) — those are owner-only "could buy" rows.
        const rows = catalog.filter(
          (a) =>
            a.word !== "ignite" &&
            (isOwner || a.available || enabled.has(a.word)),
        );
        A("div display:flex flex-direction:column gap:$1", () => {
          A.onEach(
            rows,
            ($a: ActionDef) => drawActionConfigRow($a, enabled, isOwner),
            ($a: ActionDef) => $a.id,
          );
        });
      });
    },
  });
}

function drawActionConfigRow(
  $a: ActionDef,
  enabled: Set<string>,
  isOwner: boolean,
) {
  const on = enabled.has($a.word);

  // Owner toggling an action they own: a real, clickable green checkbox whose
  // whole row (icon + name + role dots) is the <label>, so tapping anywhere
  // flips it.
  if (isOwner && $a.available) {
    S.checkbox({
      checked: on,
      inputAttrs: "accent-color:$s-success flex:none",
      label: () => drawActionLabel($a),
      change: (e: Event) =>
        store.setActionEnabled($a.word, (e.target as HTMLInputElement).checked),
    });
    return;
  }

  // Read-only row: an add-on the owner doesn't own (locked), or any action as
  // seen by a non-owner — who just needs to see what the host enabled/disabled.
  // Same checkbox as the owner's, but disabled (Staffa dims the whole label),
  // so the box width matches and the row doesn't shift between states.
  const locked = isOwner && !$a.available;
  S.checkbox({
    checked: on,
    disabled: true,
    inputAttrs: "accent-color:$s-success flex:none",
    label: () => {
      drawActionLabel($a);
      if (locked)
        A("span flex:none font-size:0.7rem letter-spacing:0.08em fg:$s-muted #LOCKED");
    },
  });
}

/** The label body shared by the owner's checkbox and the read-only rows:
 *  action icon, name + credit cost, and a coloured dot per role that can use
 *  it (colours come straight from the server catalogue). */
function drawActionLabel($a: ActionDef) {
  drawNamedIcon($a.icon, { size: "1rem" });
  A(
    "span text-transform:uppercase letter-spacing:0.04em font-size:0.82rem text=",
    $a.word + ($a.cost ? ` · ${CREDIT}${$a.cost}` : ""),
  );
  if ($a.roles.length) {
    A("span display:flex align-items:center gap:3px ml:auto", () => {
      for (const r of $a.roles) {
        A(`span flex:none w:8px h:8px r:50% background:${r.color} title=${r.label}`);
      }
    });
  }
}

/** A labelled range slider whose label updates live while dragging.
 *
 *  `value()` is the server-committed value; a local `$live` proxy follows the
 *  slider on every `input` (i.e. during the drag) so the label reflects the
 *  current position immediately, and `commit()` only fires the RPC on `change`
 *  (release). A reactive scope re-syncs `$live` whenever the committed value
 *  changes (e.g. the server echoes our commit, or another client edits it). */
function drawSlider(
  icon: (o?: IconOptions) => void,
  label: (v: number) => string,
  range: { min: number; max: number; step: number },
  value: () => number,
  commit: (v: number) => void,
) {
  A("div display:flex flex-direction:column gap:$1", () => {
    const $live = A.proxy(value());
    A(() => {
      $live.value = value();
    });
    A(
      "div display:flex align-items:center gap:$2 fg:$s-muted font-size:0.85rem letter-spacing:0.08em",
      () => {
        icon({ size: "0.95rem" });
        A(() => A(`span text=`, label($live.value)));
      },
    );
    A(
      `input type=range min=${range.min} max=${range.max} step=${range.step} value=`,
      "" + value(),
      "input=",
      (e: Event) => ($live.value = Number((e.target as HTMLInputElement).value)),
      "change=",
      (e: Event) => commit(Number((e.target as HTMLInputElement).value)),
    );
  });
}

function drawRoster(me: Me | undefined, game: Game, isOwner: boolean) {
  A("div display:flex flex-direction:column gap:$2", () => {
    drawSectionHeader("PLAYERS", { icon: users });
    S.box({
      attrs: ".primary",
      contentAttrs: "display:flex flex-direction:column gap:$2 p:$2",
      content: () => {
        A.onEach(
          game.players,
          ($p: GamePlayer) => {
            A(
              "div display:flex align-items:center justify-content:space-between gap:$2",
              () => {
                A("div", () => {
                  const isMe = $p.id === me?.currentPlayer?.id;
                  const isHost = $p.id === game.owner?.id;
                  A(
                    "span font-weight:700 text-transform:uppercase text=",
                    $p.name || "PLAYER",
                  );
                  if (isHost) A("span font-size:0.75rem fg:$s-warning # · HOST");
                  if (isMe) A("span font-size:0.75rem fg:$s-primary # · YOU");
                });
                if (isOwner && $p.id !== me?.currentPlayer?.id) {
                  S.button({
                    content: "KICK",
                    icon: () => userX({ size: "1em" }),
                    attrs: ".danger .outlined .small",
                    click: () => store.kickPlayer($p.id),
                  });
                }
              },
            );
          },
          ($p: GamePlayer) => $p.name ?? "",
        );
      },
    });
  });
}

// ── Active game (grace / running) ─────────────────────────────────────────
function drawActiveGame(me: Me | undefined, game: Game) {
  A(
    "div.s-s.neutral display:flex flex-direction:column h:100vh overflow:hidden",
    () => {
      drawHud(me, game);
      drawRosterGrouped(me, game);
      A("div flex:1 min-height:0 display:flex flex-direction:column", () => {
        drawMessageLog();
      });
      A("div flex:none ph:$2 pb:$2", () => drawAudioDebug());
      drawActionTiles(me);
    },
  );
}

function drawLeaveGameButton() {
  S.button({
    attrs: ".primary w:100% h:100% min-width:0 justify-content:space-between gap:$1 ph:$2 pv:$2",
    icon: () => logOut({ size: "1.1em" }),
    click: () => openLeaveGameDialog(),
    content: () => {
      A(
        "div font-weight:700 text-transform:uppercase letter-spacing:0.06em flex:1 min-width:0 text-align:left overflow:hidden text-overflow:ellipsis white-space:nowrap text=",
        "LEAVE GAME",
      );
    },
  });
}

function openLeaveGameDialog() {
  S.dialog({
    header: "Leave current game?",
    attrs: "width:min(420px,92vw)",
    content: (close) => {
      A("div display:flex flex-direction:column gap:$3", () => {
        A(
          "div fg:$s-muted text-align:center #Leaving this game marks you as eliminated and lets the other players continue.",
        );
        A("div display:flex justify-content:flex-end gap:$2", () => {
          S.button({
            content: "CANCEL",
            attrs: ".small .outlined",
            click: () => close(),
          });
          S.button({
            content: "LEAVE GAME",
            attrs: ".danger .small",
            click: () => {
              close();
              store.leaveGame();
            },
          });
        });
      });
    },
  });
}

function drawHud(me: Me | undefined, game: Game) {
  const player = me?.currentPlayer;
  // position:relative + z-index makes the header its own stacking context, so the
  // floated radar (which is taller than the header and dips below the divider)
  // paints over the roster beneath it.
  A(
    "div position:relative z-index:1 display:flex align-items:flex-start justify-content:space-between p:$3 pb:$2 border-bottom: 1px solid $s-faint; gap:$3",
    () => {
      // Left column: role title, time/credits
      A("div display:flex flex-direction:column gap:$1 flex:1 min-width:0", () => {
        A(() => {
          const info = roleInfo(game, player);
          const label = info?.name || "—";
          const color = info?.color || "#888888";
          A(`div display:flex align-items:center gap:$2 color:${color}`, () => {
            if (info?.icon) drawNamedIcon(info.icon, { size: "1.5rem" });
            // mt:0 cancels staffa's default heading top-margin (the h1 isn't the
            // first child here, so it would otherwise be pushed below the icon).
            A(
              `h1 mt:0 font-size:1.6rem text-transform:uppercase letter-spacing:0.12em color: ${color}; text-shadow: 0 0 8px ${color}, 0 0 18px ${hexToRgba(color, 0.5)}; #${label}`,
            );
          });
        });
        A(
          "div display:flex align-items:baseline gap:$2 font-size:0.7rem fg:$s-muted letter-spacing:0.08em",
          () => {
            A(() => A(`span fg:$s-primary font-weight:800 #${formatGameTime(game)}`));
            A(() =>
              A(
                `span color:${CREDIT_COLOR} font-weight:800 #• ${CREDIT}${player?.credits ?? 0}`,
              ),
            );
          },
        );
      });

      // Radar scope — floated near the window's top-right corner (with a little
      // margin), taller than the header so it overflows below the divider.
      A("div position:absolute right:$2 top:$2 z-index:2 line-height:0", () => A(() => drawRadar(game)));
    },
  );
}

/** Per-game display info (name/colour/icon) for a player's role, looked up from
 *  the streamed Game.roles snapshot by the player's roleId. Undefined before a
 *  role is assigned (e.g. the waiting room) or for an unknown role. */
type RoleInfo = { name: string; color: string; icon: string };
function roleInfo(game: Game, p: { roleId?: string } | undefined): RoleInfo | undefined {
  const id = p?.roleId;
  const roles = game.roles as Record<string, RoleInfo> | undefined;
  return id && roles ? roles[id] : undefined;
}
/** Resolve a role's colour, with a muted default for the unassigned/unknown. */
function roleColor(game: Game, p: { roleId?: string } | undefined): string {
  return roleInfo(game, p)?.color || "#888888";
}

/** Resolve a status label's color: server-provided or muted default. */
function statusColor(p: { statusColor?: string } | undefined): string {
  return p?.statusColor || "#888888";
}

/** Convert "#RRGGBB" to "rgba(R, G, B, A)" so we can layer translucent glow. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return `rgba(0,255,65,${alpha})`;
  const r = parseInt(m[1], 16),
    g = parseInt(m[2], 16),
    b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Absolute ms at which the current phase's timer expires, reconstructed from the
 *  streamed anchor + offset: startTime (a Date) + phaseEndTimeOffset (ms). 0 when
 *  there's no running clock (e.g. the lobby). The server keeps the phase durations
 *  to itself — the client only ever adds an offset to the anchor. */
export function phaseEndMsOf(
  game: { startTime?: Date | null; phaseEndTimeOffset?: number } | undefined,
): number {
  const st = game?.startTime;
  return st ? st.getTime() + (game?.phaseEndTimeOffset ?? 0) : 0;
}

/** Render the game's countdown as "T-3:50" (or "G-0:30" during grace). */
function formatGameTime(game: Game): string {
  const prefix = (game.timerLabel || "").toLowerCase() === "grace" ? "G-" : "T-";
  return prefix + formatRemaining(phaseEndMsOf(game));
}

/** Device-compass heading, falling back to GPS course when no magnetometer. */
function compassHeading(): number | null {
  const c = $compass.heading;
  if (c != null) return c;
  const h = store.$.fix?.heading;
  return typeof h === "number" ? h : null;
}

// Radar palette: a dark-green banded scope face with brighter-green overlays.
// The two band greens are close together so the banding stays subtle.
const RADAR_BAND_HI = "#134a24"; // band peak (lighter dark green)
const RADAR_BAND_LO = "#0b3017"; // band trough (darker green)
const RADAR_FG = "#33d957"; // bright-green overlays/markers

// Rapid on/off toggle driving the blinking player dot. State-driven (rather than
// a CSS/SMIL animation) so it keeps blinking even as the radar re-renders on
// every compass tick.
const $blink = A.proxy(true);
setInterval(() => {
  $blink.value = !$blink.value;
}, 200);

/** Top-down radar scope. North is up. The background is a banded green radial
 *  gradient (bright→dark, snapping back to bright several times) that reads as
 *  range rings. Foreground is almost-black: a small open ring for the player's
 *  position, plus a 60° field-of-view wedge in the facing direction (device
 *  compass), clipped to the scope. */
function drawRadar(game: Game, size = 102) {
  const cx = size / 2,
    cy = size / 2;
  const R = size / 2 - 1; // scope radius (fills the box)
  const Rz = R * 0.8; // where the zone boundary maps to
  const gid = `rg${size}`; // gradient id (size is unique per call site)
  const cid = `rc${size}`; // clip id

  A(() => {
    const fix = store.$.fix;
    const center = game.center as { lat: number; lng: number } | undefined;
    const cLat = center?.lat ?? 0,
      cLng = center?.lng ?? 0;
    const haveCenter = !!(fix && center);
    const zoneR = (game.radiusM as number) || 200;

    let d = 0,
      brg = 0;
    if (haveCenter) {
      d = distM(fix!.lat, fix!.lng, cLat, cLng);
      brg = bearingDeg(cLat, cLng, fix!.lat, fix!.lng); // centre → player
    }

    A(`svg width=${size} height=${size} viewBox="0 0 ${size} ${size}"`, () => {
      A("defs", () => {
        // Banded radial gradient: each band fades bright→dark, then snaps back to
        // bright at the next stop, giving concentric range-ring stripes.
        A(`radialGradient id=${gid} cx=50% cy=50% r=50%`, () => {
          const bands = 3;
          for (let i = 0; i < bands; i++) {
            const lo = ((i / bands) * 100).toFixed(2);
            const hi = (((i + 1) / bands) * 100).toFixed(2);
            A(`stop offset=${lo}% stop-color=${RADAR_BAND_HI}`);
            A(`stop offset=${hi}% stop-color=${RADAR_BAND_LO}`);
          }
        });
        A(`clipPath id=${cid}`, () => {
          A(`circle cx=${cx} cy=${cy} r=${R.toFixed(1)}`);
        });
      });

      // Scope face + outline (dimmed so the rim doesn't outshine the overlays).
      A(`circle cx=${cx} cy=${cy} r=${R.toFixed(1)} fill=url(#${gid}) stroke=${RADAR_FG} stroke-opacity=0.45 stroke-width=1.5`);
      // Cardinal labels (north up), just inside the rim.
      const lr = R * 0.82;
      const fs = (size * 0.12).toFixed(1);
      for (const [lbl, lx, ly] of [
        ["N", cx, cy - lr],
        ["E", cx + lr, cy],
        ["S", cx, cy + lr],
        ["W", cx - lr, cy],
      ] as const) {
        A(`text x=${lx.toFixed(1)} y=${ly.toFixed(1)} text-anchor=middle dominant-baseline=central font-size=${fs} font-weight=700 fill=${RADAR_FG} #${lbl}`);
      }
      if (!haveCenter) return;

      // Player position: zone radius maps to Rz; clamp inside the rim.
      const pr = Math.min(R - 5, (d / zoneR) * Rz);
      const rad = (brg * Math.PI) / 180;
      const px = cx + pr * Math.sin(rad);
      const py = cy - pr * Math.cos(rad);

      // 60° field-of-view wedge in the facing direction, clipped to the scope.
      const hdg = compassHeading();
      if (hdg != null) {
        const half = (30 * Math.PI) / 180;
        const hr = (hdg * Math.PI) / 180;
        const L = 2 * R; // overshoot the rim; the clip trims it to the arc
        const a1 = hr - half,
          a2 = hr + half;
        const p1x = (px + L * Math.sin(a1)).toFixed(1);
        const p1y = (py - L * Math.cos(a1)).toFixed(1);
        const p2x = (px + L * Math.sin(a2)).toFixed(1);
        const p2y = (py - L * Math.cos(a2)).toFixed(1);
        A(
          `polygon points="${px.toFixed(1)},${py.toFixed(1)} ${p1x},${p1y} ${p2x},${p2y}" fill=${RADAR_FG} fill-opacity=0.35 clip-path=url(#${cid})`,
        );
      }

      // Player position: a rapidly blinking dot, drawn last so it sits on top of
      // the cardinal labels, with a dark border so it stays legible even when it
      // overlaps a (bright-green) letter.
      if ($blink.value) {
        A(`circle cx=${px.toFixed(1)} cy=${py.toFixed(1)} r=3.5 fill=${RADAR_FG} stroke=#04130a stroke-width=1.2`);
      }
    });
  });
}

function drawRosterGrouped(me: Me | undefined, game: Game) {
  A("div display:flex flex-direction:column gap:$1 p:$2 ph:$3", () => {
    A(() => {
      // Group by roleId; each group's display name/colour/icon comes from the
      // per-game roles snapshot (unassigned players fall under the "—" bucket).
      const groups = new Map<string, GamePlayer[]>();
      for (const p of game.players ?? []) {
        const k = p.roleId || "—";
        const list = groups.get(k) ?? [];
        list.push(p);
        groups.set(k, list);
      }
      for (const [, players] of groups) {
        const info = roleInfo(game, players[0]);
        const color = info?.color || "#888888";
        const groupLabel = info?.name || "—";
        const groupIcon = info?.icon;
        A(
          "div display:flex align-items:baseline gap:$1 flex-wrap:wrap line-height:1.5",
          () => {
            if (groupIcon)
              A(`span align-self:center color:${color}`, () =>
                drawNamedIcon(groupIcon, { size: "0.9rem" }),
              );
            A(
              `span font-weight:700 text-transform:uppercase letter-spacing:0.08em color:${color} #${groupLabel}:`,
            );
            for (const p of players) {
              const isMe = p.id === me?.currentPlayer?.id;
              const pColor = roleColor(game, p);
              A(`span color:${pColor} text-transform:uppercase #${p.name || "?"}`);
              if (isMe) {
                // No trailing ';' after bg: the unquoted value runs to the next
                // space, so a ';' would become part of the colour and be ignored
                // (leaving black-on-dark, unreadable).
                A(
                  `span font-size:0.65rem font-weight:800 ph:4px pv:1px r:2px bg:${pColor} fg:#04130a text-transform:uppercase #YOU`,
                );
              }
              if (p.statusLabel) {
                A(
                  `span font-size:0.7rem pv:1px ph:4px r:2px border: 1px solid ${statusColor(p)}; color:${statusColor(p)} text-transform:uppercase #${p.statusLabel}`,
                );
              }
            }
          },
        );
      }
    });
  });
}

function drawMessageLog() {
  const container = A(
    "div.s-s.primary flex:1 min-height:0 display:flex flex-direction:column p:$2 ph:$3 gap:$1 overflow-y:auto",
    () => {
      A(() => {
        const entries = store.$.log;
        if (!entries.length) {
          A(
            "div fg:$s-muted text-align:center font-size:0.7rem letter-spacing:0.1em p:$3 #— NO TRANSMISSIONS —",
          );
          return;
        }
        for (const e of entries) drawMessageLogEntry(e);
      });
    },
  );

  // Keep the newest transmission in view: whenever the number of log entries
  // changes, scroll to the bottom on the next frame (so the new rows have been
  // laid out and scrollHeight reflects them).
  A(() => {
    void store.$.log.length; // observe inserts/clears
    if (container)
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
  });
}

function drawMessageLogEntry(e: {
  id: number;
  t: number;
  phase: string;
  phaseEndMs: number;
  text: string;
  system: boolean;
}) {
  A(
    "div display:flex align-items:baseline gap:$2 font-size:0.8rem letter-spacing:0.04em p:$1 r:2px",
    () => {
      A(() => {
        const prefix = e.phase === "grace" ? "G-" : "T-";
        // Remaining time at the moment the message was logged.
        const remainingMs = Math.max(0, e.phaseEndMs - e.t);
        const s = Math.floor(remainingMs / 1000);
        const m = Math.floor(s / 60);
        const ss = s % 60;
        const tag = `${prefix}${m}:${String(ss).padStart(2, "0")}`;
        A(
          `span fg:$s-muted font-weight:700 letter-spacing:0.08em min-width:5em #${tag}`,
        );
        A(`span color:${e.system ? "$s-warning" : "$s-primary"} #${e.text}`);
      });
    },
  );
}

function drawActionTiles(me: Me | undefined) {
  A("div display:flex flex-direction:column border-top: 1px solid $s-faint;", () => {
    if (!bridge.capabilities().speech) {
      A(
        "div ph:$3 pv:$1 font-size:0.65rem fg:$s-muted letter-spacing:0.08em text-align:center #— VOICE COMMANDS UNAVAILABLE — TAP TO ACT —",
      );
    } else {
      // Push-to-talk status: recognition only listens during a window opened by a
      // headset play/pause press, so tell the user that and light up while open.
      A(() => {
        if (store.$.listening) {
          A(
            "div ph:$3 pv:$1 font-size:0.7rem fg:$s-primary font-weight:800 letter-spacing:0.12em text-align:center #◉ LISTENING — SPEAK NOW",
          );
        } else {
          A(
            "div ph:$3 pv:$1 font-size:0.65rem fg:$s-muted letter-spacing:0.08em text-align:center #— PRESS HEADSET BUTTON TO SPEAK, OR TAP —",
          );
        }
      });
    }
    A(
      "div display:grid gap:$2 p:$3",
      "grid-template-columns: repeat(2, 1fr);",
      () => {
        A.onEach(
          store.$.actions,
          ($a: ActionDef) => drawActionTile($a, me),
          ($a: ActionDef) => $a.id,
        );
        drawLeaveGameButton();
      },
    );
  });
}

function drawActionTile($a: ActionDef, me: Me | undefined) {
  S.button({
    // min-width:0 lets the button shrink inside its grid cell on narrow screens;
    // without it the cell stays as wide as its content and the second column gets
    // clipped off the right edge.
    attrs: ".primary w:100% h:100% min-width:0 justify-content:space-between gap:$1 ph:$2 pv:$2",
    icon: () => drawNamedIcon($a.icon, { size: "1.15rem" }),
    click: () => store.invokeAction($a.id),
    content: () => {
      // Command name (left-aligned, bold uppercase). Truncates with an ellipsis
      // rather than forcing the tile wider than its column.
      A(
        "div font-weight:700 text-transform:uppercase letter-spacing:0.06em flex:1 min-width:0 text-align:left overflow:hidden text-overflow:ellipsis white-space:nowrap text=",
        $a.word.toUpperCase(),
      );
      // Cost / time-left (right-aligned, never shrinks).
      A(() => {
        const activeEnd = me?.currentPlayer?.countdowns?.[$a.id] ?? 0;
        const remainingMs = activeEnd - $now.value;
        if (remainingMs > 0) {
          const s = Math.ceil(remainingMs / 1000);
          const m = Math.floor(s / 60);
          const r = s % 60;
          A(
            `div flex:none font-size:0.75rem fg:$s-primary font-weight:700 letter-spacing:0.08em #${m}:${String(r).padStart(2, "0")}`,
          );
        } else if ($a.cost) {
          // Free actions show no cost badge at all; priced ones show ₡N, red
          // when unaffordable as a deliberate "can't pay" signal.
          const credits = me?.currentPlayer?.credits ?? 0;
          const affordable = credits >= $a.cost;
          const color = affordable ? CREDIT_COLOR : "$s-danger";
          A(
            `div flex:none font-size:0.75rem font-weight:700 letter-spacing:0.08em color:${color} #${CREDIT}${$a.cost}`,
          );
        }
      });
    },
  });
}

// ── Ended ──────────────────────────────────────────────────────────────────
/** The player's standing on the over/spectator screen: a big label, a subtext,
 *  and a colour. Win/lose is game logic, so the server decides it and streams the
 *  whole banner as `endStatus` (VICTORY / GAME OVER / ELIMINATED) — the client
 *  just displays it. "LEFT GAME" is the one purely-local state (the server can't
 *  tell a voluntary leave from an elimination), so it overrides here. */
function overStatus(
  me: Me | undefined,
  _game: Game,
): { label: string; sub: string; color: string } {
  if (store.$.leftGame)
    return { label: "LEFT GAME", sub: "You left the operation.", color: "#9aa0a6" };
  const es = me?.currentPlayer?.endStatus;
  if (es) return { label: es.label, sub: es.sub, color: es.color };
  // Fallback if the banner hasn't streamed yet (shouldn't linger).
  return { label: "GAME OVER", sub: "", color: "#FF3322" };
}

/**
 * The spectator / game-over screen: the regular game layout (HUD + roster/log),
 * but with the action tiles replaced by a clear status indicator and a single
 * Leave/Back button. Once the game has *finished* (phase "ended"), the roster is
 * replaced by the mission-replay radar driven by the streamed activityLog.
 */
function drawOverScreen(me: Me | undefined, game: Game) {
  const finished = game.phase === "ended";
  A(
    "div.s-s.neutral display:flex flex-direction:column h:100vh overflow:hidden",
    () => {
      drawHud(me, game);
      drawOverStatus(me, game);
      if (finished) {
        drawReplay(game);
      } else {
        drawRosterGrouped(me, game);
        A("div flex:1 min-height:0 display:flex flex-direction:column", () => {
          drawMessageLog();
        });
      }
      drawOverActions(game);
    },
  );
}

function drawOverStatus(me: Me | undefined, game: Game) {
  A(() => {
    const s = overStatus(me, game);
    A(
      "div flex:none display:flex flex-direction:column align-items:center gap:2px ph:$3 pb:$2",
      () => {
        A(
          `div font-weight:800 font-size:1.5rem letter-spacing:0.14em text-transform:uppercase color: ${s.color}; text-shadow: 0 0 10px ${hexToRgba(s.color, 0.6)}; #${s.label}`,
        );
        if (s.sub)
          A(
            `div font-size:0.78rem fg:$s-muted text-align:center text-transform:uppercase letter-spacing:0.05em #${s.sub}`,
          );
      },
    );
  });
}

function drawOverActions(game: Game) {
  const terminal = store.$.leftGame || game.phase === "ended";
  A(
    "div flex:none display:flex flex-direction:column border-top: 1px solid $s-faint; p:$3",
    () => {
      S.button({
        content: terminal ? "BACK TO LOBBY" : "LEAVE GAME",
        icon: () => (terminal ? arrowLeft : logOut)({ size: "1.2em" }),
        attrs: ".large w:100%",
        click: () => store.returnToLobby(),
      });
    },
  );
}

// ── Mission replay ───────────────────────────────────────────────────────────
type ReplayEntry = NonNullable<Game["activityLog"]>[number];

/** The replay panel: a header + the animated radar, gated on activityLog (which
 *  the server sets and streams exactly once, at game end). */
function drawReplay(game: Game) {
  A("div flex:1 min-height:0 display:flex flex-direction:column gap:$1 p:$2", () => {
    drawSectionHeader("MISSION REPLAY", { icon: radio });
    A(() => {
      const log = (game.activityLog ?? []) as ReplayEntry[];
      if (!log.length) {
        A(
          "div flex:1 display:flex align-items:center justify-content:center fg:$s-muted font-size:0.8rem letter-spacing:0.1em #— GENERATING REPLAY —",
        );
        return;
      }
      drawReplayCanvas(game, log);
    });
  });
}

/**
 * A canvas-based, looping replay of the whole match at ~10× speed, driven by the
 * activityLog. Each player's path grows as a trail of dots (colour derived from
 * their role, tinted per-player); when the playhead reaches an action, its id is
 * written near that player's position in their colour and fades over one second.
 * After the trails complete they linger for 3 s, then everything clears and the
 * loop restarts.
 */
function drawReplayCanvas(game: Game, log: ReplayEntry[]) {
  const players = game.players ?? [];
  const center = game.center as { lat: number; lng: number } | undefined;
  const cLat = center?.lat ?? 0;
  const cLng = center?.lng ?? 0;
  const mPerLat = 111_000;
  const mPerLng = 111_000 * Math.cos((cLat * Math.PI) / 180);

  // Build per-player location paths (metres east/north of centre) + action
  // markers anchored to each player's last known position, with absolute times.
  type Pt = { t: number; xm: number; ym: number };
  type Act = { t: number; playerIndex: number; text: string; xm: number; ym: number };
  const paths = new Map<number, Pt[]>();
  const acts: Act[] = [];
  const lastLoc = new Map<number, { xm: number; ym: number }>();
  let t = 0;
  let ext = (game.radiusM as number) || 100;
  for (const e of log) {
    t += e.timeDelta;
    if (typeof e.what === "object") {
      const xm = (e.what.lng - cLng) * mPerLng;
      const ym = (e.what.lat - cLat) * mPerLat;
      let arr = paths.get(e.playerIndex);
      if (!arr) paths.set(e.playerIndex, (arr = []));
      arr.push({ t, xm, ym });
      lastLoc.set(e.playerIndex, { xm, ym });
      ext = Math.max(ext, Math.abs(xm), Math.abs(ym));
    } else {
      const loc = lastLoc.get(e.playerIndex) ?? { xm: 0, ym: 0 };
      acts.push({ t, playerIndex: e.playerIndex, text: e.what, xm: loc.xm, ym: loc.ym });
    }
  }
  const totalMs = t || 1;
  ext = ext * 1.12 || 100;

  const colorFor = (idx: number) =>
    tintColor(roleInfo(game, players[idx])?.color || RADAR_FG, idx);

  const canvas = A(
    "canvas display:block width:100% flex:1 min-height:0 background:#0a1a10 border-radius:8px",
  ) as unknown as HTMLCanvasElement;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // ~10× real time, but never a sub-4 s blink for a very short match.
  const REAL_MS = Math.max(4000, totalMs / 10);
  const LINGER_MS = 3000;
  const cycle = REAL_MS + LINGER_MS;

  const project = (
    xm: number,
    ym: number,
    cx: number,
    cy: number,
    R: number,
  ): [number, number] => [cx + (xm / ext) * R, cy - (ym / ext) * R];

  let raf = 0;
  let start: number | null = null;

  const frame = (ts: number) => {
    if (start == null) start = ts;
    const elapsed = (ts - start) % cycle;
    // Playhead grows to the end during REAL_MS, then holds through the linger.
    const playMs = elapsed <= REAL_MS ? (elapsed / REAL_MS) * totalMs : totalMs;

    const cssW = canvas.clientWidth || 300;
    const cssH = canvas.clientHeight || 300;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const cx = cssW / 2;
    const cy = cssH / 2;
    const R = Math.min(cssW, cssH) / 2 - 12;

    // Backdrop: the shrinking zone ring + centre pip.
    ctx.strokeStyle = "rgba(120,220,150,0.22)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, Math.min(R, (((game.radiusM as number) || 100) / ext) * R), 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();

    // Player trails, growing up to the playhead.
    for (const [idx, pts] of paths) {
      const color = colorFor(idx);
      let last: [number, number] | null = null;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      for (const p of pts) {
        if (p.t > playMs) break;
        const xy = project(p.xm, p.ym, cx, cy, R);
        if (last) ctx.lineTo(xy[0], xy[1]);
        else ctx.moveTo(xy[0], xy[1]);
        last = xy;
      }
      ctx.stroke();
      if (!last) continue;
      // Bright head dot at the current position.
      ctx.globalAlpha = 1;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(last[0], last[1], 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#04130a";
      ctx.lineWidth = 1.2;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Action ids: appear as the playhead passes, then fade over ~1 real second.
    ctx.font = "700 11px ui-monospace, monospace";
    ctx.textAlign = "center";
    for (const a of acts) {
      const at = (a.t / totalMs) * REAL_MS; // real-time offset of this action
      const age = elapsed - at;
      if (age < 0 || age >= 1000) continue;
      const xy = project(a.xm, a.ym, cx, cy, R);
      ctx.globalAlpha = 1 - age / 1000;
      ctx.fillStyle = colorFor(a.playerIndex);
      ctx.fillText(a.text.toUpperCase(), xy[0], xy[1] - 8);
    }
    ctx.globalAlpha = 1;

    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  A.clean(() => cancelAnimationFrame(raf));
}

/** "#rrggbb" → [h(0-360), s(0-100), l(0-100)]. */
function hexToHsl(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return [140, 70, 55];
  const r = parseInt(m[1], 16) / 255,
    g = parseInt(m[2], 16) / 255,
    b = parseInt(m[3], 16) / 255;
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b),
    d = mx - mn;
  const l = (mx + mn) / 2;
  let h = 0,
    s = 0;
  if (d) {
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    h =
      mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return [h, s * 100, l * 100];
}

/** [h,s,l] → "#rrggbb". */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const mm = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (hh < 1) [r, g] = [c, x];
  else if (hh < 2) [r, g] = [x, c];
  else if (hh < 3) [g, b] = [c, x];
  else if (hh < 4) [g, b] = [x, c];
  else if (hh < 5) [r, b] = [x, c];
  else [r, b] = [c, x];
  const to = (v: number) =>
    Math.round((v + mm) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** A per-player variant of a role colour: same family, distinguished by a small
 *  index-keyed hue rotation + lightness shift, so two hunters (both red) are
 *  still tellable apart on the replay. */
function tintColor(hex: string, idx: number): string {
  const [h, s, l] = hexToHsl(hex);
  const nh = h + (idx % 2 === 0 ? 1 : -1) * (12 + 10 * Math.floor(idx / 2));
  const nl = Math.max(35, Math.min(72, l + (((idx % 3) - 1) * 10)));
  return hslToHex(nh, Math.max(45, s), nl);
}

// ── helpers ────────────────────────────────────────────────────────────────
function formatRemaining(phaseEndMs: number | undefined): string {
  if (!phaseEndMs) return "--:--";
  const ms = Math.max(0, phaseEndMs - $now.value);
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}
